import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Search,
  FileText,
  Check,
  Users,
  Leaf,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Copy
} from 'lucide-react'
import toast from 'react-hot-toast'
import { usePriceLists, useCopyPriceList } from '../../hooks/useSupabase'

type SortField = 'name' | 'type' | 'is_default' | 'article_count'
type SortDirection = 'asc' | 'desc'

export function PriceLists() {
  const navigate = useNavigate()
  const { data: priceLists = [], isLoading, error } = usePriceLists()
  const copyPriceList = useCopyPriceList()
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<'all' | 'customer' | 'subcontractor'>('all')
  const [sortField, setSortField] = useState<SortField>('type')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const handleCopy = async (e: React.MouseEvent, priceList: typeof priceLists[0]) => {
    e.stopPropagation() // Prevent row click
    const newName = prompt('Ange namn för den nya prislistan:', `${priceList.name} (kopia)`)
    if (!newName) return

    try {
      const newPriceList = await copyPriceList.mutateAsync({ id: priceList.id, newName })
      toast.success('Prislista kopierad')
      navigate(`/admin/pricelists/${newPriceList.id}`)
    } catch (error) {
      console.error('Error copying price list:', error)
      toast.error('Kunde inte kopiera prislista')
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 text-nature-400" />
    return sortDirection === 'asc'
      ? <ArrowUp className="h-4 w-4 text-primary-600" />
      : <ArrowDown className="h-4 w-4 text-primary-600" />
  }

  const filteredAndSortedPriceLists = useMemo(() => {
    let result = [...priceLists]

    // Filter by search
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase()
      result = result.filter(pl =>
        pl.name.toLowerCase().includes(lowerSearch) ||
        pl.description?.toLowerCase().includes(lowerSearch)
      )
    }

    // Filter by type
    if (typeFilter !== 'all') {
      result = result.filter(pl => pl.type === typeFilter)
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name, 'sv')
          break
        case 'type':
          comparison = a.type.localeCompare(b.type)
          break
        case 'is_default':
          comparison = (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0)
          break
        case 'article_count':
          comparison = a.article_count - b.article_count
          break
      }
      return sortDirection === 'asc' ? comparison : -comparison
    })

    return result
  }, [priceLists, searchTerm, typeFilter, sortField, sortDirection])

  const handlePriceListClick = (id: string) => {
    // Save filtered IDs to sessionStorage for navigation
    const ids = filteredAndSortedPriceLists.map(pl => pl.id)
    sessionStorage.setItem('filtered-pricelist-ids', JSON.stringify(ids))
    navigate(`/admin/pricelists/${id}`)
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-500">
        Ett fel uppstod vid hämtning av prislistor
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">Prislistor</h1>
          <p className="text-sm text-nature-600 dark:text-nature-400 mt-1">
            Hantera prislistor för kunder och trädgårdsmästare
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/pricelists/new')}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Lägg till</span>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-nature-400" />
          <input
            type="text"
            placeholder="Sök prislistor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setTypeFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              typeFilter === 'all'
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-300 hover:bg-nature-200 dark:hover:bg-nature-600'
            }`}
          >
            Alla
          </button>
          <button
            onClick={() => setTypeFilter('customer')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              typeFilter === 'customer'
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                : 'bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-300 hover:bg-nature-200 dark:hover:bg-nature-600'
            }`}
          >
            <Users className="h-4 w-4" />
            Kunder
          </button>
          <button
            onClick={() => setTypeFilter('subcontractor')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              typeFilter === 'subcontractor'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : 'bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-300 hover:bg-nature-200 dark:hover:bg-nature-600'
            }`}
          >
            <Leaf className="h-4 w-4" />
            Trädgårdsmästare
          </button>
        </div>
      </div>

      {/* Price Lists Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nature-600 mx-auto"></div>
            <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar prislistor...</p>
          </div>
        </div>
      ) : filteredAndSortedPriceLists.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-nature-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-nature-900 dark:text-nature-100 mb-2">
            {searchTerm || typeFilter !== 'all' ? 'Inga prislistor hittades' : 'Inga prislistor än'}
          </h3>
          <p className="text-nature-600 dark:text-nature-400 mb-4">
            {searchTerm || typeFilter !== 'all'
              ? 'Prova att ändra sökord eller filter'
              : 'Skapa din första prislista för att komma igång'}
          </p>
          {!searchTerm && typeFilter === 'all' && (
            <button
              onClick={() => navigate('/admin/pricelists/new')}
              className="btn-primary"
            >
              Skapa prislista
            </button>
          )}
        </div>
      ) : (
        <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-nature-50 dark:bg-nature-700/50">
              <tr>
                <th
                  onClick={() => handleSort('name')}
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                >
                  <div className="flex items-center space-x-1">
                    <span>Namn</span>
                    {getSortIcon('name')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('type')}
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                >
                  <div className="flex items-center space-x-1">
                    <span>Typ</span>
                    {getSortIcon('type')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('is_default')}
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                >
                  <div className="flex items-center space-x-1">
                    <span>Standard</span>
                    {getSortIcon('is_default')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('article_count')}
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                >
                  <div className="flex items-center space-x-1">
                    <span>Artiklar</span>
                    {getSortIcon('article_count')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                  Användning
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                  Åtgärder
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-nature-200 dark:divide-nature-700">
              {filteredAndSortedPriceLists.map((priceList) => (
                <tr
                  key={priceList.id}
                  onClick={() => handlePriceListClick(priceList.id)}
                  className="hover:bg-nature-50 dark:hover:bg-nature-700/50 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-nature-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-nature-900 dark:text-nature-100">
                          {priceList.name}
                        </div>
                        {priceList.description && (
                          <div className="text-sm text-nature-500 dark:text-nature-400 truncate max-w-xs">
                            {priceList.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      priceList.type === 'customer'
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                        : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                    }`}>
                      {priceList.type === 'customer' ? (
                        <>
                          <Users className="h-3 w-3 mr-1" />
                          Kund
                        </>
                      ) : (
                        <>
                          <Leaf className="h-3 w-3 mr-1" />
                          Trädgårdsmästare
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {priceList.is_default && (
                      <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-nature-600 dark:text-nature-400">
                    {priceList.article_count} st
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1 text-xs">
                      {priceList.type === 'customer' && priceList.customer_count > 0 && (
                        <span className="inline-flex items-center text-blue-600 dark:text-blue-400">
                          <Users className="h-3 w-3 mr-1" />
                          {priceList.customer_count} kund{priceList.customer_count > 1 ? 'er' : ''}
                        </span>
                      )}
                      {priceList.type === 'subcontractor' && priceList.subcontractor_count > 0 && (
                        <span className="inline-flex items-center text-green-600 dark:text-green-400">
                          <Leaf className="h-3 w-3 mr-1" />
                          {priceList.subcontractor_count} trädgårdsmästare
                        </span>
                      )}
                      {((priceList.type === 'customer' && priceList.customer_count === 0) ||
                        (priceList.type === 'subcontractor' && priceList.subcontractor_count === 0)) && (
                        <span className="text-nature-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${
                      priceList.is_active
                        ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                        : 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200'
                    }`}>
                      {priceList.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={(e) => handleCopy(e, priceList)}
                      className="p-2 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors text-nature-600 dark:text-nature-400 hover:text-primary-600 dark:hover:text-primary-400"
                      title="Kopiera prislista"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
